import { useEffect, useRef } from 'react';

export const LINKEDIN_PROFILE_AUTO_CHECK_ATTEMPTS = 20;
export const LINKEDIN_PROFILE_AUTO_CHECK_WINDOW_MS = 2000;

const wait = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

type UseLinkedInProfileAutoCheckParams = {
    enabled: boolean;
    profileFound: boolean;
    onCheck: () => void | Promise<void>;
};

/** Polls LinkedIn profile detection — same as clicking Refresh, 20× over 2 seconds. */
export const useLinkedInProfileAutoCheck = ({
    enabled,
    profileFound,
    onCheck,
}: UseLinkedInProfileAutoCheckParams): void => {
    const onCheckRef = useRef(onCheck);
    onCheckRef.current = onCheck;

    useEffect(() => {
        if (!enabled || profileFound) return;

        let cancelled = false;
        const intervalMs = LINKEDIN_PROFILE_AUTO_CHECK_WINDOW_MS / LINKEDIN_PROFILE_AUTO_CHECK_ATTEMPTS;

        const runChecks = async () => {
            for (let attempt = 0; attempt < LINKEDIN_PROFILE_AUTO_CHECK_ATTEMPTS && !cancelled; attempt++) {
                if (process.env.NODE_ENV === 'development') {
                    console.debug(
                        `[LinkedIn auto-check] refresh ${attempt + 1}/${LINKEDIN_PROFILE_AUTO_CHECK_ATTEMPTS}`
                    );
                }

                await onCheckRef.current();

                if (attempt < LINKEDIN_PROFILE_AUTO_CHECK_ATTEMPTS - 1 && !cancelled) {
                    await wait(intervalMs);
                }
            }
        };

        void runChecks();

        return () => {
            cancelled = true;
        };
    }, [enabled, profileFound]);
};
